import React, { useEffect, useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { addDoc, collection, doc, Firestore, setDoc } from 'firebase/firestore';
import { getUsersFromFirestore, User } from '../../components/utils/UserUtils';
import { db } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { randomScrambleForEvent } from 'cubing/scramble';

interface FormState {
    player1: string;
    player2: string;
}

const MatchCreate: React.FC = () => {
    const navigate = useNavigate();
    
    const [users, setUsers] = useState<User[]>([]);
    const [form, setForm] = useState<FormState>({
        player1: '',
        player2: ''
    });

    useEffect(() => {
        // Usage
        getUsersFromFirestore(db).then((users:User[]) => {
            setUsers(users)
        }).catch(error => {
            console.error('Error fetching users:', error);
        });
    }, [])


    const handleSelectChange = (field: keyof FormState) => (event: SelectChangeEvent) => {
        setForm((prev) => ({
            ...prev,
            [field]: event.target.value as string
        }));
    };

    const handleSubmit = async () => {
        if (!form.player1 || !form.player2) {
            alert('Please fill out all fields.');
            return;
        }
        if (form.player1 === form.player2) {
            alert('Please select two different players.');
            return;
        }

        const player1 = users.find(p=>p.uid === form.player1);
        const player2 = users.find(p=>p.uid === form.player2);
            
        const matchId = generateRandomHexCode();
        
        const matchDocRef = doc(db, "matches", matchId);
        await setDoc(matchDocRef, {
            createdAt: Date.now(),
            currentRound: 0,
            currentSolve: 0,
            status: "scheduled",
            winner: -1,
            uids: [form.player1, form.player2],
            streamids: ["48ksE8Yoe74wCeiO09QFsP", "57YEnsYCbCWLYz6TdAPCsI"]
        });
     
        await setDoc(doc(collection(matchDocRef, "players"), player1?.uid), player1)
        await setDoc(doc(collection(matchDocRef, "players"), player2?.uid), player2)

        for(var i=0; i<5; i++){
            const roundsRef = doc(collection(matchDocRef, "rounds"), `Round ${i + 1}`);
            await setDoc(roundsRef, {
                scrambles: await generateScrambles(8)
            })
        }

        navigate(`/matches/${matchId}/admin`);
    };

    return (
        
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            {users.length > 0 && <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Create New Matchup
                </Typography>

                <Grid container spacing={2}>

                    <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="player1-label">Player 1</InputLabel>
                            <Select
                                labelId="player1-label"
                                label="Player 1"
                                value={form.player1}
                                onChange={handleSelectChange('player1')}
                            >
                                {users.map((player) => (
                                    <MenuItem key={player.uid} value={player.uid}>
                                        {player.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="player2-label">Player 2</InputLabel>
                            <Select
                                labelId="player2-label"
                                label="Player 2"
                                value={form.player2}
                                onChange={handleSelectChange('player2')}
                            >
                                {users.map((player) => (
                                    <MenuItem key={player.uid} value={player.uid}>
                                        {player.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} textAlign="right" sx={{ mt: 2 }}>
                        <Button variant="contained" color="primary" onClick={handleSubmit}>
                            Create Match
                        </Button>
                    </Grid>
                </Grid>
            </Paper>}
        </Container>
    );
};

async function generateScrambles(count: number) : Promise<string[]> {
    const scrambles: string[] = []

    for(var i=0; i<count; i++){
        const alg = await randomScrambleForEvent("333")
        scrambles.push(alg.toString())
    }
    
    return scrambles
}

function generateRandomHexCode(): string {
    const randomHex = () => Math.floor(Math.random() * 16).toString(16).toUpperCase(); // Generates a single hex digit
  
    const segment = () => Array.from({ length: 3 }, randomHex).join(''); // Generates a 3-character hex segment
  
    return `${segment()}-${segment()}`; // Combines two segments with a dash
  }

export default MatchCreate;
